import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { hideMessage, showMessage } from '~/actions';
import { Column, Typography } from '@tempmee/react';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { useNavigationDetector, useToggle } from '~/hooks';
import {
  IJobPostDetailEntity,
  JobPostingStatus,
  useSaveJobPostDetail,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { getEffectiveProfession, isExcludedProfession } from '../../Constants';
import { useJobDetailsForm } from '../../JobDetailsForm/hooks/useJobDetailsForm';
import { descriptionSchema, generalSchema, scheduleSchema } from '../../JobDetailsForm/schema';
import { JobDetailsFormMapper } from '../../JobDetailsForm/mappers/JobDetailsFormMapper';
import { IBaseFormProps } from '../../JobDetailsForm/Types';
import {
  GeneralJobInfoEditable,
  JobDescriptionEditable,
  ScheduleEditable,
} from '../../JobDetailsForm';

interface IJobDetailsTabViewProps {
  jobPosting?: IJobPostDetailEntity;
}

enum EditableSection {
  GeneralInfo = 'generalInfo',
  ScheduleInfo = 'scheduleInfo',
  JobDescription = 'jobDescription',
}

const sectionSchemas = {
  [EditableSection.GeneralInfo]: generalSchema,
  [EditableSection.ScheduleInfo]: scheduleSchema,
  [EditableSection.JobDescription]: descriptionSchema,
} as const;

type TReadonlyFields = NonNullable<IBaseFormProps['readonlyFields']>;

/** General Info fields a live posting renders as plain text instead of as inputs. */
const generalInfoReadonlyFields: TReadonlyFields = ['office', 'professionType'];

/**
 * Same, minus the profession, for when the profession in play is one the
 * permanent-jobs flow does not support. `generalSchema` rejects those, so leaving
 * the field readonly would dead-end the office: Save stays disabled on every edit
 * to the section and there is no control to correct the profession with.
 */
const generalInfoReadonlyFieldsWithEditableProfession: TReadonlyFields = ['office'];

/**
 * Same again, minus the office, for a posting that never had one.
 *
 * `generalSchema.office` is required, and `JobDetailsFormMapper.fromEntity` seeds
 * `office: null` whenever `officeSelected` is false — so on those postings the
 * office is a second, invisible reason Save can never release, and unlocking the
 * profession alone leaves the remediation just as dead.
 *
 * The two cohorts are the same people, not a hypothetical overlap: a live posting
 * can only carry an excluded profession by way of the Copy & Paste import flow,
 * and that flow yields `officeSelected: false` for precisely parent offices
 * (`createDraftJobPost` sends `isOfficeSelected: false` for them, and neither the
 * parsed posting nor Confirm Job Details ever fills an office in).
 *
 * Unlocking is the honest fix rather than relaxing the rule: the posting genuinely
 * has no office, and a parent office is exactly who `GeneralJobInfoForm` renders
 * the office picker for, so the field it exposes is one they can actually satisfy.
 */
const generalInfoNoReadonlyFields: TReadonlyFields = [];

export const JobDetailsTabView = ({ jobPosting }: IJobDetailsTabViewProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [editSection, setEditSection] = useState<EditableSection | null>(null);
  const [confirmationExitModalOpen, toggleConfirmationExitModal] = useToggle(false);

  const { mutate: saveJobPostDetail, isPending } = useSaveJobPostDetail();

  // Only allow editing when job posting status is active
  const isEditable = useMemo(
    () => jobPosting?.status === JobPostingStatus.Active,
    [jobPosting?.status],
  );

  const {
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { isValid, isDirty, errors },
  } = useJobDetailsForm({
    initialValues: jobPosting,
    schema: editSection ? sectionSchemas[editSection] : undefined,
  });

  const professionValue = useWatch({ control, name: 'professionType' });
  const officeValue = useWatch({ control, name: 'office' });

  /**
   * Whether the profession field has to be unlocked, checked against the posting
   * AND the value currently in the form.
   *
   * The posting alone is not enough. `useJobDetailsForm` seeds `defaultValues`
   * once at mount and never re-seeds, so after the office corrects an excluded
   * profession and saves, the refetched posting is supported while `reset()` —
   * Cancel, or discarding on navigation — still restores the excluded profession
   * the form mounted with. Keying only off the posting would re-lock the field
   * over that value and rebuild the exact dead-end this unlock exists to remove.
   *
   * The form value alone is not enough either: it is empty for a moment on mount,
   * and reading the posting is what keeps the field unlocked while the office is
   * mid-edit rather than making the select vanish the instant they pick a
   * supported profession.
   */
  const hasExcludedProfession = useMemo(
    () =>
      isExcludedProfession(getEffectiveProfession(jobPosting)) ||
      isExcludedProfession(professionValue),
    [jobPosting, professionValue],
  );

  /**
   * Whether the posting has no office to satisfy `generalSchema.office` with —
   * read from the posting AND the form for the same reasons as the profession
   * above: the posting keeps the picker up while the office is mid-edit, and the
   * form value keeps it up after `reset()` restores the `null` the form mounted
   * with.
   */
  const hasNoOffice = useMemo(
    () => !jobPosting?.officeSelected || !officeValue,
    [jobPosting?.officeSelected, officeValue],
  );

  const generalInfoFieldsToLock = useMemo(() => {
    if (!hasExcludedProfession) return generalInfoReadonlyFields;

    return hasNoOffice
      ? generalInfoNoReadonlyFields
      : generalInfoReadonlyFieldsWithEditableProfession;
  }, [hasExcludedProfession, hasNoOffice]);

  // Navigation blocking logic - block when there are unsaved changes
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: isDirty && editSection !== null, // Only block when editing and there are changes
    onNavigationAttempt: () => {
      // Show confirmation modal for any navigation attempt with unsaved changes
      toggleConfirmationExitModal.setOn();
    },
  });

  const handleCancelEdit = useCallback(() => {
    reset();
    setEditSection(null);
  }, [reset]);

  const handleSaveEdit = handleSubmit((data) => {
    if (!jobPosting?.id) return;

    const payload = JobDetailsFormMapper.toSaveJobPostDetailPayload(data);

    saveJobPostDetail(
      { id: jobPosting.id, payload },
      {
        onSuccess: () => {
          // Invalidate query to refetch latest data in parent component
          queryClient.invalidateQueries({
            queryKey: ['GET_JOB_POSTING_DETAIL', jobPosting.id],
          });

          setEditSection(null);
          setTimeout(() => {
            dispatch(
              showMessage({
                popupProps: {
                  rightButtonText: 'Close',
                  rightButtonAction: () => dispatch(hideMessage()),
                },
                title: 'Changes Saved!',
                message: 'Your updates have been saved successfully.',
                isSuccess: true,
              }),
            );
          }, 500);
        },
        onError: (error) => {
          showErrorMessage({ error });
        },
      },
    );
  });

  return (
    <Column gap={8} width="892px" className="mt-2 mx-auto">
      {/* Temporarily hidden as it’s not included in the MVP */}
      {/* <Row
        alignSelf="center"
        alignItems="center"
        border={`1.5px solid ${Colors.neutral_100}`}
        borderRadius={6}
        paddingInline={65}
        paddingBlock={12}
        gap={42}
      >
        <Row gap={6}>
          <Text fontSize={18}>Views:</Text>
          <Text fontSize={20} bold>
            1672
          </Text>
        </Row>
        <Divider width={2} height={24} marginVertical={0} />
        <Row gap={6}>
          <Text fontSize={18}>Applied:</Text>
          <Text fontSize={20} bold>
            77
          </Text>
        </Row>
        <Divider width={2} height={24} marginVertical={0} />
        <Row gap={6}>
          <Text fontSize={18}>Interviewed:</Text>
          <Text fontSize={20} bold>
            10
          </Text>
        </Row>
      </Row> */}

      <Column gap={4}>
        <Typography as="span" size="heading_4" bold>
          Job Details
        </Typography>

        {/*
          General Job Information Section.

          `errors` is part of the IBaseFormProps contract and this was the only
          call site dropping it, so it is passed for consistency — not because the
          office can see it fire. Nothing writes errors here today: `mode` defaults
          to 'onSubmit', so only handleSubmit populates them, and handleSubmit is
          reachable only through Save, which is disabled while the form is invalid.
          The prop starts doing real work the moment `mode` or `disableSave`
          changes. Schedule and Description are deliberately left as they are —
          same dead contract, and widening it is not this fix's job.
        */}
        <GeneralJobInfoEditable
          readonlyFields={generalInfoFieldsToLock}
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.GeneralInfo}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.GeneralInfo)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          errors={errors}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.GeneralInfo)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />

        {/* Schedule Section */}
        <ScheduleEditable
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.ScheduleInfo}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.ScheduleInfo)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.ScheduleInfo)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />

        {/* Job Description Section */}
        <JobDescriptionEditable
          jobPosting={jobPosting}
          editMode={editSection === EditableSection.JobDescription}
          hideEditButton={
            !isEditable || (!!editSection && editSection !== EditableSection.JobDescription)
          }
          disableSave={!isValid || !isDirty || isPending}
          control={control}
          setValue={setValue}
          onEditClick={() => setEditSection(EditableSection.JobDescription)}
          onCancelClick={handleCancelEdit}
          onSaveClick={handleSaveEdit}
        />
      </Column>

      <LeaveInterviewCreationPopup
        isOpen={confirmationExitModalOpen}
        onDiscard={() => {
          reset();
          setEditSection(null);
          toggleConfirmationExitModal.setOff();
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          clearPendingNavigation();
          toggleConfirmationExitModal.setOff();
        }}
      />
    </Column>
  );
};
